import { useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, ArrowUpRight, LogOut, LayoutDashboard } from "lucide-react";
import { NAV } from "../data/site";
import { useAuth } from "../context/AuthContext";
import { EASE } from "./Reveal";

const Logo = () => (
  <Link to="/" data-testid="logo-link" className="flex items-center" aria-label="Costruzioni Ciosko - Home">
    <img src="/images/logo-light.png" alt="Costruzioni Ciosko" className="h-11 w-auto" />
  </Link>
);

const UserMenu = () => {
  const { user, isAdmin, loading, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  if (loading) return null;
  if (!user) {
    return (
      <Link to="/accedi" data-testid="header-login-button" className="btn-ghost !px-4 !py-2.5 !text-[11px] hidden sm:inline-flex">
        Accedi
      </Link>
    );
  }
  return (
    <div className="relative">
      <button
        data-testid="header-user-menu"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 border border-steel p-1 pr-3 hover:border-forest-glow transition-colors"
        aria-label="Menu account"
        aria-expanded={open}
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-7 h-7 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-7 h-7 rounded-full bg-forest flex items-center justify-center text-xs font-bold text-white">
            {(user.name || user.email)[0].toUpperCase()}
          </span>
        )}
        <span className="text-xs text-metal max-w-[90px] truncate hidden md:block">{user.name || user.email}</span>
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="header-user-dropdown"
            className="absolute right-0 top-full mt-2 w-56 bg-card border border-steel shadow-2xl z-50"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2 }}
          >
            <p className="px-4 py-3 text-xs text-cement border-b border-steel truncate">{user.email}</p>
            {isAdmin && (
              <button
                data-testid="header-admin-panel"
                onClick={() => { setOpen(false); navigate("/admin"); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-metal hover:text-forest-glow hover:bg-graphite transition-colors text-left"
              >
                <LayoutDashboard size={15} /> Pannello admin
              </button>
            )}
            <button
              data-testid="header-logout"
              onClick={async () => { setOpen(false); await logout(); navigate("/"); }}
              className="w-full flex items-center gap-3 px-4 py-3 text-sm text-metal hover:text-red-400 hover:bg-graphite transition-colors text-left border-t border-steel"
            >
              <LogOut size={15} /> Esci
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const { user, isAdmin, login, logout } = useAuth();

  useEffect(() => setOpen(false), [location.pathname]);
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    fn();
    window.addEventListener("scroll", fn, { passive: true });
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 border-b ${
          scrolled ? "bg-base/85 backdrop-blur-xl border-steel" : "bg-transparent border-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-10 h-[72px] flex items-center justify-between">
          <Logo />
          <nav className="hidden lg:flex items-center gap-5" aria-label="Navigazione principale">
            {NAV.slice(1).map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                data-testid={`nav-${item.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative text-[11px] font-semibold uppercase tracking-[0.14em] transition-colors duration-300 hover:text-white ${
                    isActive ? "text-forest-glow" : "text-metal/80"
                  }`
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-3">
            <UserMenu />
            <Link to="/contatti" data-testid="header-quote-button" className="btn-primary !px-5 !py-2.5 hidden xl:inline-flex">
              Richiedi preventivo
            </Link>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden w-11 h-11 flex items-center justify-center border border-steel text-metal hover:border-forest-glow hover:text-forest-glow transition-colors"
              aria-label={open ? "Chiudi menu" : "Apri menu"}
              aria-expanded={open}
            >
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            className="fixed inset-0 z-40 bg-base/98 backdrop-blur-xl flex flex-col justify-center px-8 overflow-y-auto"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35 }}
          >
            <nav className="flex flex-col gap-1 py-24" aria-label="Menu mobile">
              {NAV.map((item, i) => (
                <motion.div
                  key={item.to}
                  initial={{ opacity: 0, x: -28 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.05 * i + 0.08, duration: 0.45, ease: EASE }}
                >
                  <NavLink
                    to={item.to}
                    data-testid={`mobile-nav-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `group flex items-center justify-between border-b border-steel/60 py-3.5 font-display font-bold uppercase text-xl tracking-tight transition-colors ${
                        isActive ? "text-forest-glow" : "text-white hover:text-forest-glow"
                      }`
                    }
                  >
                    {item.label}
                    <ArrowUpRight size={18} className="text-cement group-hover:text-forest-glow transition-colors" />
                  </NavLink>
                </motion.div>
              ))}
            </nav>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5, ease: EASE }}
              className="pb-28 space-y-3"
            >
              {user ? (
                <div className="flex gap-3">
                  {isAdmin && <Link to="/admin" data-testid="mobile-menu-admin" className="btn-ghost flex-1 !py-3 text-center">Admin</Link>}
                  <button data-testid="mobile-menu-logout" onClick={logout} className="btn-ghost flex-1 !py-3">Esci</button>
                </div>
              ) : (
                <button data-testid="mobile-menu-login" onClick={login} className="btn-ghost w-full !py-3">Accedi con Google</button>
              )}
              <Link to="/contatti" data-testid="mobile-menu-quote-button" className="btn-primary w-full">
                Richiedi un preventivo
              </Link>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
